import { Collapsible } from '@ui/animated/Collapsible'
import AnimatedEmpty from '@ui/animated/AnimatedEmpty'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { LoadingFooter } from '@ui/lists/LoadingFooter'
import ButtonsFixed from '@ui/buttons/ButtonsFixed'
import { ErpTemple } from '@ca/common'
import Page from '@shell/Page'
import Dropdown from '@ui/elements/Dropdown'
import Search from './Search'
import Header from '@ui/elements/Header'
import ConfirmationModal from '@ui/components/ConfirmationModal'
import useModalDataState from '@order/hooks/useModalDataState'
import { useOrderState } from '@order/hooks/useOrderState'
import useStartOrderConfig from '@order/hooks/useStartOrderConfig'
import { FlashList } from '@shopify/flash-list'
import TempleItem from '@frame/Frame/Temple'
import { router } from 'expo-router'
import { useCustomerTemples, useTempleActions } from 'hooks/temple/useTemples'
import ConfigDebugModal from 'features/debug/ConfigDebugModal'
import useFlattenInfiniteData from 'hooks/useFlattenInfiniteData'
import { useRefreshByUser } from 'hooks/useRefreshByUser'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Animated from 'react-native-reanimated'
import cn from '@ui/utils/cn'

export default function FrameTempleSelect() {
  const { t } = useTranslation(['frame', 'modals', 'ui'])
  const text = 'frame:temple.select'

  const { goToStock } = useStartOrderConfig()

  const order = useOrderState((state) => state.order)
  const setTemple = useOrderState((state) => state.setTemple)
  const removeConfirm = useModalDataState<string | null>({ data: null })

  const selected = useMemo(() => {
    return order.items.find((item) => item.id === order.activeItemId)
  }, [order.items, order.activeItemId])

  const list = useCustomerTemples()
  const { remove } = useTempleActions()

  const { isRefetchingByUser, refetchByUser } = useRefreshByUser(
    list.refetch,
    list.isRefetching
  )

  const isLoadings = list.isFetching || list.isLoading

  const items = useFlattenInfiniteData(list.data)

  const showLoading = isLoadings
  const showEmpty = !list.isLoading && list.isFetched && items.length === 0
  const showItems = items.length > 0

  const styleLoading = useAnimatedOpacity(showLoading)
  const styleEmpty = useAnimatedOpacity(showEmpty)
  const styleItems = useAnimatedOpacity(showItems)

  const toggleSelect = useCallback(
    (temple: ErpTemple) => {
      if (!selected) {
        return
      }

      const isSelected = selected?.temple?.id === temple.id

      if (isSelected) {
        setTemple(selected.id, undefined)
      } else {
        setTemple(selected.id, temple)
      }
    },
    [selected, setTemple]
  )

  const { data: removeId, close: removeConfirmClose } = removeConfirm

  const handleConfirmRemove = useCallback(() => {
    if (!removeId) {
      return
    }
    remove.mutate({ id: removeId })
    removeConfirmClose()
  }, [remove, removeId, removeConfirmClose])

  const onAddTemple = useCallback(() => {
    router.push('/frame/temple/search')
  }, [])

  return (
    <>
      <Page stage="frame" list>
        {/* {isEmpty && <Redirect href="/frame/temple/search" />} */}
        <FlashList
          ListHeaderComponent={
            <>
              <Header
                list
                title={t(`${text}.header.title`)}
                desc={t(`${text}.header.desc`)}
              />
              <Collapsible open={showLoading} fullWidth>
                <LoadingFooter style={styleLoading} />
              </Collapsible>
            </>
          }
          ListFooterComponent={null}
          ListEmptyComponent={
            <AnimatedEmpty style={styleEmpty}>
              <Search />
            </AnimatedEmpty>
          }
          data={items}
          numColumns={1}
          keyboardShouldPersistTaps="handled"
          onEndReachedThreshold={0.25}
          onEndReached={list.hasNextPage ? list.fetchNextPage : undefined}
          onRefresh={refetchByUser}
          refreshing={isRefetchingByUser}
          progressViewOffset={100}
          extraData={selected?.temple?.id}
          renderItem={({ item }) => (
            <Animated.View style={styleItems} className={cn('px-8 pb-8')}>
              <TempleItem
                temple={item.temple}
                isChecked={selected?.temple?.id === item.temple.id}
                dropdown={
                  <Dropdown
                    icon={{ name: 'dots' }}
                    offsetX={8}
                    popoverProps={{
                      offset: 20,
                      className: 'absolute right-6 top-6',
                    }}
                    items={[
                      {
                        label: t('ui:removeTemple'),
                        destructive: true,
                        onPress: () => removeConfirm.open(item.id),
                      },
                    ]}
                  />
                }
                onPress={() => toggleSelect(item.temple)}
                config={{ isSearchScreen: false }}
              />
            </Animated.View>
          )}
        />
        <ConfirmationModal
          open={removeConfirm.isOpen}
          onClose={removeConfirm.close}
          onConfirm={handleConfirmRemove}
          title={t('modals:temple.remove.title')}
          desc={t('modals:temple.remove.desc')}
        />
      </Page>
      <ConfigDebugModal />
      <ButtonsFixed
        buttons={[
          {
            variant: 'outlined',
            label: t('ui:addTemple'),
            onPress: onAddTemple,
          },
          {
            variant: 'filled',
            disabled: !selected?.temple,
            label: t('ui:nextStep'),
            iconEnd: 'arrow-narrow-right',
            onPress: goToStock,
          },
        ]}
      />
    </>
  )
}
